import { useEffect, useState } from "react"
import { useTranslations } from "../../lib/translations"
import { api } from "../../lib/api"

export function SettingsTab({ agent }) {
  const t = useTranslations()
  const [metrics, setMetrics] = useState(null)
  const [thresholds, setThresholds] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    api.get(`/api/v1/agents/${agent.id}/success_metrics`)
      .then((body) => {
        if (cancelled) return
        setMetrics(body.metrics || [])
        setThresholds((body.metrics && body.metrics[0] && body.metrics[0].thresholds) || null)
      })
      .catch((e) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [agent.id])

  return (
    <div className="cm-tabwrap">
      <div className="cm-coming-soon" style={{ marginBottom: 16 }}>{t("modal.settings.coming_soon")}</div>

      <AutonomyPanel metrics={metrics} thresholds={thresholds} error={error} t={t} />

      <div style={{ fontFamily: "Space Mono", fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.9, marginTop: 24 }}>
        <div>role · <b style={{ color: "var(--ink-2)" }}>{agent.role}</b></div>
        <div>status · <b style={{ color: "var(--ink-2)" }}>{agent.status}</b></div>
        <div>master · <b style={{ color: "var(--ink-2)" }}>{agent.master ? "yes" : "no"}</b></div>
        {agent.manager_id && <div>manager · <b style={{ color: "var(--ink-2)" }}>{agent.manager_id}</b></div>}
        {agent.model && <div>model · <b style={{ color: "var(--ink-2)" }}>{agent.model}</b></div>}
        {agent.anthropic_agent_id && <div>anthropic_agent_id · <b style={{ color: "var(--ink-2)" }}>{agent.anthropic_agent_id}</b></div>}
      </div>
    </div>
  )
}

function AutonomyPanel({ metrics, thresholds, error, t }) {
  const intro = thresholds
    ? t("modal.settings.autonomy.intro", {
        up_rate:   Math.round(thresholds.slow_up_hit_rate * 100),
        up_days:   thresholds.slow_up_window_days,
        down_rate: Math.round(thresholds.fast_down_hit_rate * 100),
        down_hours: thresholds.fast_down_window_hours,
      })
    : null

  return (
    <section style={{ border: "1px solid var(--line)", borderRadius: 16, padding: 14, background: "var(--card)" }}>
      <div style={{ fontFamily: "Fraunces", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
        {t("modal.settings.autonomy.title")}
      </div>
      {intro && (
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 10 }}>{intro}</div>
      )}

      {error && <div style={{ color: "var(--bad)", fontSize: 12 }}>{error}</div>}
      {!error && metrics === null && <div style={{ color: "var(--ink-3)", fontSize: 12 }}>…</div>}
      {!error && metrics && metrics.length === 0 && (
        <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{t("modal.settings.autonomy.empty")}</div>
      )}
      {!error && metrics && metrics.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--ink-3)", borderBottom: "1px solid var(--line)" }}>
              <th style={{ padding: "6px 8px" }}>{t("modal.settings.autonomy.col_kind")}</th>
              <th style={{ padding: "6px 8px" }}>{t("modal.settings.autonomy.col_state")}</th>
              <th style={{ padding: "6px 8px" }}>{t("modal.settings.autonomy.col_hit_rate")}</th>
              <th style={{ padding: "6px 8px" }}>{t("modal.settings.autonomy.col_attempts")}</th>
              <th style={{ padding: "6px 8px" }}>{t("modal.settings.autonomy.col_window")}</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <MetricRow key={m.id} metric={m} t={t} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function MetricRow({ metric, t }) {
  const stateColor = ({
    earned_auto:       "var(--ok)",
    auto_revoked:      "var(--bad)",
    requires_sign_off: "var(--ink-2)",
  })[metric.autonomy_state] || "var(--ink-3)"

  const stateLabel = t(`modal.settings.autonomy.state.${metric.autonomy_state}`) || metric.autonomy_state
  const hitPct     = `${Math.round((metric.hit_rate || 0) * 100)}%`
  const windowEnd  = metric.window_end ? new Date(metric.window_end).toLocaleDateString() : "—"

  return (
    <tr style={{ borderBottom: "1px solid var(--line)" }}>
      <td style={{ padding: "8px 8px", fontFamily: "Space Mono", color: "var(--ink-2)" }}>{metric.task_kind}</td>
      <td style={{ padding: "8px 8px", color: stateColor, fontWeight: 600 }}>{stateLabel}</td>
      <td style={{ padding: "8px 8px", fontFamily: "Space Mono" }}>{hitPct}</td>
      <td style={{ padding: "8px 8px", fontFamily: "Space Mono" }}>{metric.success_count} / {metric.total}</td>
      <td style={{ padding: "8px 8px", fontFamily: "Space Mono", color: "var(--ink-3)" }}>→ {windowEnd}</td>
    </tr>
  )
}
